import type { ServerAsset } from '@sequencehq/api/utils/commonModels'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { CubeStatus } from '../cube.domain.types'
import { match } from 'ts-pattern'
import { useQuoteApproval, useStatus } from '../selectors'
import { useAmendmentLock } from '../selectors/useAmendmentLock'
import { isQuoteSuperseded } from '../postActionStages/queries/availableFeatures/utils/quoteSuperseded'

export const useSelectBillingScheduleAttachments = (): ServerAsset[] => {
  const { queries } = useCubeContext()
  return queries.rawData.data.schedule.attachmentAssets
}

export const useStoreBillingScheduleAttachments = () => {
  const cubeContext = useCubeContext()
  return (assets: ServerAsset[]) => {
    cubeContext.mutators.updateData({ schedule: { attachmentAssets: assets } })
  }
}

const useBillingScheduleStatus = () => {
  const { queries } = useCubeContext()
  queries.rawData.data.common.status
  return queries.rawData.data.common.status
}

export const useShouldDisableModifyBillingScheduleAttachments = () => {
  const status = useBillingScheduleStatus()
  const { isLockedByAmendment } = useAmendmentLock()

  if (isLockedByAmendment) {
    return {
      reason: 'Cannot modify attachments while the schedule is being amended'
    }
  }

  if (
    [
      CubeStatus.ScheduleCancelled,
      CubeStatus.ScheduleSuspended,
      CubeStatus.ScheduleCompleted
    ].includes(status)
  ) {
    const humanReadableStatus =
      status === CubeStatus.ScheduleCancelled
        ? 'cancelled'
        : status === CubeStatus.ScheduleSuspended
          ? 'suspended'
          : 'completed'

    return {
      reason: `Cannot modify attachments for a ${humanReadableStatus} schedule`
    }
  }

  return false
}

export const useSelectQuoteAttachments = (): ServerAsset[] => {
  const { queries } = useCubeContext()
  return queries.rawData.data.quote.attachmentAssets
}

export const useStoreQuoteAttachments = () => {
  const cubeContext = useCubeContext()
  return (assets: ServerAsset[]) => {
    cubeContext.mutators.updateData({ quote: { attachmentAssets: assets } })
  }
}

export const useShouldDisableModifyQuoteAttachments = () => {
  const { status } = useStatus()
  const { isApprovalPending } = useQuoteApproval()
  const { queries } = useCubeContext()

  if (isQuoteSuperseded(queries.rawData)) {
    return {
      reason: 'Cannot modify attachments for a superseded quote'
    }
  }

  if (isApprovalPending) {
    return {
      reason: 'Cannot modify attachments while a quote is pending approval'
    }
  }

  if (
    status === CubeStatus.QuotePreparingSignatureRequest ||
    status === CubeStatus.QuoteExecuted
  ) {
    const reason = match(status)
      .with(
        CubeStatus.QuotePreparingSignatureRequest,
        () =>
          'Cannot modify attachments while a quote is preparing for signature'
      )
      .with(
        CubeStatus.QuoteExecuted,
        () => 'Cannot modify attachments for an executed quote'
      )
      .exhaustive()

    return { reason }
  }

  return false
}
